<template>
  <form @submit.prevent="handleSubmit" class="daily-stat-form">
    <div class="form-field">
      <IntercomDateInput
        v-model="formData.stat_date"
        :label="$t('dailyStats.date')"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model.number="formData.meals_served"
        :label="$t('dailyStats.mealsServed')"
        type="number"
        placeholder="0"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model.number="formData.total_expense"
        :label="$t('dailyStats.totalExpense')"
        type="number"
        placeholder="0.00"
        :disabled="loading"
      />
      <p class="field-hint">{{ $t('dailyStats.expenseHint') }}</p>
    </div>

    <div v-if="formData.meals_served > 0 && formData.total_expense > 0" class="cost-per-meal">
      <span class="cost-label">{{ $t('dailyStats.costPerMeal') }}:</span>
      <span class="cost-value">{{ formatCompactCurrency(costPerMeal) }}</span>
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.notes"
        :label="$t('dailyStats.notes')"
        placeholder="Optional notes about today"
        :disabled="loading"
      />
    </div>

    <div class="form-actions">
      <IntercomButton type="button" variant="ghost" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton type="submit" variant="primary" :loading="loading">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomInput,
  IntercomDateInput,
  IntercomButton,
} from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  stat: { type: Object, default: null },
  date: { type: String, default: null },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel'])

const formData = reactive({
  stat_date: props.date || dayjs().format('YYYY-MM-DD'),
  meals_served: null,
  total_expense: null,
  notes: '',
})

const costPerMeal = computed(() => {
  if (!formData.meals_served || !formData.total_expense) return 0
  return formData.total_expense / formData.meals_served
})

// Populate form when stat prop changes (edit mode)
watch(() => props.stat, (newStat) => {
  if (newStat) {
    formData.stat_date = newStat.stat_date
      ? dayjs(newStat.stat_date).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD')
    formData.meals_served = newStat.meals_served || null
    formData.total_expense = newStat.total_expense || null
    formData.notes = newStat.notes || ''
  }
}, { immediate: true })

// Update date when prop changes
watch(() => props.date, (newDate) => {
  if (newDate && !props.stat) {
    formData.stat_date = newDate
  }
})

const handleSubmit = () => {
  emit('submit', { ...formData })
}
</script>

<style scoped>
.daily-stat-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.field-hint {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
}

.cost-per-meal {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--color-gray-50);
  border-radius: var(--radius-md);
}

.cost-label {
  color: var(--color-gray-600);
  font-size: var(--font-size-sm);
}

.cost-value {
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}
</style>
